import { TRANSCRIPT_PENDING_MAX_AGE_MS } from '@consts';
import {
  MediaitemFileModel,
  MediaitemFileQuality,
  MediaitemFileRole,
  MediaitemModel
} from '@models';
import { BlockContentModel, BlockModel } from '@models/tasks';
import * as Sentry from '@sentry/nextjs';

export const downloadMediaitemFile = async (
  mediaitemFile: MediaitemFileModel,
  filename: string = mediaitemFile.originalFilename ?? ''
): Promise<void> => {
  try {
    const response = await fetch(mediaitemFile.url);
    if (!response.ok) {
      throw new Error(`Download failed with status ${response.status}`);
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    Sentry.captureException(error, { extra: { url: mediaitemFile.url } });
    const link = document.createElement('a');
    link.href = mediaitemFile.url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

class MediaitemService {
  private mediaitem: MediaitemModel;

  constructor(mediaitem: MediaitemModel) {
    this.mediaitem = mediaitem;
  }

  getReadyFiles(): { [key: string]: MediaitemFileModel } {
    if (!this.mediaitem.files) {
      return {};
    }

    return {
      original: this.mediaitem.files.find((file) => file.role === MediaitemFileRole.ORIGINAL),
      thumbnail: this.mediaitem.files.find(
        (file) => file.role === MediaitemFileRole.THUMBNAIL && file.ready
      ),
      display240p: this.mediaitem.files.find(
        (file) =>
          file.role === MediaitemFileRole.DISPLAY &&
          file.quality === MediaitemFileQuality.QUALITY_240P &&
          file.ready
      ),
      display480p: this.mediaitem.files.find(
        (file) =>
          file.role === MediaitemFileRole.DISPLAY &&
          file.quality === MediaitemFileQuality.QUALITY_480P &&
          file.ready
      ),
      display720p: this.mediaitem.files.find(
        (file) =>
          file.role === MediaitemFileRole.DISPLAY &&
          file.quality === MediaitemFileQuality.QUALITY_720P &&
          file.ready
      ),
      vimeo: this.mediaitem.files.find(
        (file) =>
          file.role === MediaitemFileRole.DISPLAY &&
          file.quality === MediaitemFileQuality.VIMEO &&
          file.ready
      )
    };
  }

  getImageSrc(withThumbnail: boolean = false): string {
    const files = this.getReadyFiles();
    if (withThumbnail && files.thumbnail) {
      return files.thumbnail.url;
    } else if (files.display720p) {
      return files.display720p.url;
    } else if (files.display480p) {
      return files.display480p.url;
    } else if (files.display240p) {
      return files.display240p.url;
    } else if (files.original) {
      return files.original.url;
    }

    return '';
  }

  getVideoSrc() {
    const files = this.mediaitem.files;
    const display720pMP4 = files.find(
      (file) =>
        file.role === MediaitemFileRole.DISPLAY &&
        file.quality === MediaitemFileQuality.QUALITY_720P &&
        file.ready
    );
    const display480pMP4 = files.find(
      (file) =>
        file.role === MediaitemFileRole.DISPLAY &&
        file.quality === MediaitemFileQuality.QUALITY_480P &&
        file.ready
    );
    const display240pMP4 = files.find(
      (file) =>
        file.role === MediaitemFileRole.DISPLAY &&
        file.quality === MediaitemFileQuality.QUALITY_240P &&
        file.ready
    );
    const vimeo = files.find(
      (file) =>
        file.role === MediaitemFileRole.DISPLAY &&
        file.quality === MediaitemFileQuality.VIMEO &&
        file.ready
    );

    const original = files.find((file) => file.role === MediaitemFileRole.ORIGINAL);
    return (
      vimeo?.url ||
      display720pMP4?.url ||
      display480pMP4?.url ||
      display240pMP4?.url ||
      original?.url
    );
  }

  isVimeoSource(): boolean {
    if (!this.mediaitem.files) {
      return false;
    }

    const vimeoFile = this.mediaitem.files.find(
      (file) =>
        file.role === MediaitemFileRole.DISPLAY &&
        file.quality === MediaitemFileQuality.VIMEO &&
        file.ready
    );

    return !!vimeoFile;
  }

  getDuration(): number {
    const files = this.mediaitem.files;
    const original = files.find((file) => file.role === MediaitemFileRole.ORIGINAL);

    return original?.duration || 0;
  }

  getThumbnail(): string | null {
    const files = this.getReadyFiles();
    if (files.thumbnail) {
      return files.thumbnail.url;
    }
    return null;
  }

  getAspectRatio(): number {
    const files = this.getReadyFiles();
    if (files.thumbnail?.width && files.thumbnail?.height) {
      return files.thumbnail.width / files.thumbnail.height;
    } else if (files.display720p?.width && files.display720p?.height) {
      return files.display720p.width / files.display720p.height;
    } else if (files.display480p?.width && files.display480p?.height) {
      return files.display480p.width / files.display480p.height;
    } else if (files.display240p?.width && files.display240p?.height) {
      return files.display240p.width / files.display240p.height;
    } else if (files.original?.width && files.original?.height) {
      return files.original.width / files.original.height;
    }
    return 1;
  }

  getHeight(): number {
    const files = this.getReadyFiles();
    return files.original?.height || 0;
  }

  getWidth(): number {
    const files = this.getReadyFiles();
    return files.original?.width || 0;
  }

  getRotation(): number {
    const files = this.getReadyFiles();
    return files.original?.rotation || 0;
  }

  // Age must not settle anything here — a slow transcript is still on its way.
  isTranscriptSettled(): boolean {
    const transcript = this.mediaitem.files?.find(
      (file) => file.role === MediaitemFileRole.TRANSCRIPT
    );
    return !transcript || transcript.ready;
  }

  isTranscriptPending(): boolean {
    const transcript = this.mediaitem.files?.find(
      (file) => file.role === MediaitemFileRole.TRANSCRIPT
    );
    if (!transcript || transcript.ready) return false;

    const createdAt = new Date(this.mediaitem.createdAt).getTime();
    if (!Number.isFinite(createdAt)) return false;

    return Date.now() - createdAt < TRANSCRIPT_PENDING_MAX_AGE_MS;
  }

  getReadyTranscriptFile(): MediaitemFileModel | null {
    return (
      this.mediaitem.files?.find(
        (file) => file.role === MediaitemFileRole.TRANSCRIPT && file.ready
      ) ?? null
    );
  }
}

const collectMediaitemIds = (
  source: BlockContentModel | BlockModel[] | null | undefined
): number[] => {
  const ids: number[] = [];

  const walk = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (value === null || typeof value !== 'object') return;

    Object.entries(value).forEach(([key, nested]) => {
      if (key === 'mediaitemId') {
        if (typeof nested === 'number' && !ids.includes(nested)) ids.push(nested);
        return;
      }
      walk(nested);
    });
  };

  walk(source);
  return ids;
};

export { collectMediaitemIds, MediaitemService };
